import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from '@/components/v2/ui/Dialog'
import { Button } from '@/components/v2/ui/Button'
import { Input } from '@/components/v2/ui/Input'
import { SegmentConditionList } from './SegmentConditionList'
import { SegmentLiveCount } from './SegmentLiveCount'
import { SegmentTopNRow } from './SegmentTopNRow'
import { useSegmentRuleState } from './useSegmentRuleState'
import { CHANNEL_CAPABLE_FIELDS } from './segmentPresets'
import type { ProductSegmentRule } from './segmentPresets'
import type { ProductTableFilters } from '@/hooks/useProductTableConfig'

interface Props {
  open: boolean
  onClose: () => void
  onSave: (name: string, rule: ProductSegmentRule) => void
  /** Must be stable (memoize via useCallback in the caller), else unnecessary timer resets. */
  countFn: (filters: ProductTableFilters) => Promise<number>
  /** Channel-aware live count (raw rule, bypasses ruleToFilters); default path unchanged when unset. */
  countRuleFn?: (rule: ProductSegmentRule) => number | Promise<number>
  initialRule?: ProductSegmentRule
  initialName?: string
}

export function SegmentRuleBuilder({
  open, onClose, onSave, countFn, countRuleFn, initialRule, initialName,
}: Props) {
  const { t } = useTranslation()
  const { rule, keys, addCondition, updateCondition, removeCondition, setTop } =
    useSegmentRuleState(initialRule)
  const [name, setName] = useState(initialName ?? '')

  const hasUnresolvedChannel = rule.conditions.some(
    (c) => CHANNEL_CAPABLE_FIELDS.has(c.field) && !c.channel_id,
  )

  function handleSave() {
    if (!name.trim() || hasUnresolvedChannel) return
    onSave(name.trim(), rule)
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('productSegments.builder.title')}</DialogTitle>
        </DialogHeader>

        {/* Match toggle — ODER disabled (S2 only supports 'all') */}
        <div className="flex items-center gap-2 text-sm mt-2">
          <span className="font-medium">{t('productSegments.builder.matchAll')}</span>
          <span className="text-neutral-400 dark:text-neutral-500">
            {t('productSegments.builder.matchAny')} ({t('productSegments.soon')})
          </span>
        </div>

        <SegmentConditionList
          rule={rule}
          itemKeys={keys}
          onAdd={addCondition}
          onChange={updateCondition}
          onRemove={removeCondition}
        />

        <SegmentTopNRow
          top={rule.top}
          onToggle={(enabled) => setTop(enabled ? { metric: 'revenue_30d', n: 10 } : undefined)}
          onChange={(patch) => rule.top && setTop({ ...rule.top, ...patch })}
        />

        <SegmentLiveCount rule={rule} countFn={countFn} countRuleFn={countRuleFn} />

        <DialogFooter className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
          <Input
            data-testid="builder-name"
            placeholder={t('productSegments.builder.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label={t('productSegments.builder.name')}
          />
          <Button
            data-testid="builder-save"
            variant="primary"
            onClick={handleSave}
            disabled={!name.trim() || hasUnresolvedChannel}
          >
            {t('productSegments.builder.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

SegmentRuleBuilder.displayName = 'SegmentRuleBuilder'
